import React, { useEffect, useState, useMemo } from "react";
import { seoApi } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle, DollarSign, RotateCw, Cpu, Zap } from "lucide-react";

// ─── Status colour map (unified enum shared across the app) ──────────
const STATUS_STYLE = {
  pending:    { bg: "bg-neutral-100",  txt: "text-neutral-700", bar: "bg-neutral-400" },
  running:    { bg: "bg-blue-50",       txt: "text-blue-800",    bar: "bg-blue-500" },
  completed:  { bg: "bg-emerald-50",    txt: "text-emerald-800", bar: "bg-emerald-500" },
  warning:    { bg: "bg-amber-50",      txt: "text-amber-800",   bar: "bg-amber-500" },
  failed:     { bg: "bg-red-50",        txt: "text-red-800",     bar: "bg-red-500" },
  cancelled:  { bg: "bg-neutral-100",   txt: "text-neutral-600", bar: "bg-neutral-400" },
  skipped:    { bg: "bg-neutral-50",    txt: "text-neutral-500", bar: "bg-neutral-300" },
  retrying:   { bg: "bg-amber-50",      txt: "text-amber-800",   bar: "bg-amber-400" },
};

const EVENT_LEVEL_STYLE = {
  info:    "text-neutral-700",
  warning: "text-amber-700",
  error:   "text-red-700",
  debug:   "text-neutral-400",
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${s.bg} ${s.txt}`}
          data-testid={`stage-status-${status}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.bar}`} />
      {status}
    </span>
  );
}

function fmtMs(ms) {
  if (ms == null || ms === 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

function fmtUsd(v) {
  if (!v) return "$0.0000";
  return `$${(+v).toFixed(4)}`;
}

// ─── Per-stage timeline bar with perf comparison ─────────────────────
function StageBar({ stage, maxDurationMs, performanceStats }) {
  const style = STATUS_STYLE[stage.status] || STATUS_STYLE.completed;
  const widthPct = maxDurationMs > 0
    ? Math.max(1.5, (stage.duration_ms / maxDurationMs) * 100)
    : 1.5;

  const perf = performanceStats[stage.type];
  const perfCurrent = stage.duration_ms || 0;
  const perfAvg = perf?.avg_ms || 0;
  const regressionRatio = perfAvg > 0 ? perfCurrent / perfAvg : 1;
  const regressionBadge = perfAvg > 0
    ? (regressionRatio >= 1.5 ? { label: `${regressionRatio.toFixed(1)}× slower`, color: "text-red-700 bg-red-50" }
       : regressionRatio <= 0.6 ? { label: `${(1 / regressionRatio).toFixed(1)}× faster`, color: "text-emerald-700 bg-emerald-50" }
       : null)
    : null;

  const meta = stage.metadata || {};
  const hasWarnings = (stage.warning_count || 0) > 0;
  const hasErrors = (stage.error_count || 0) > 0;
  const hasRetries = (stage.retries || 0) > 0;
  const hasCost = (stage.cost_usd || 0) > 0;
  const hasTokens = (stage.total_tokens || (stage.tokens_input + stage.tokens_output)) > 0;

  return (
    <div className="group" data-testid={`stage-${stage.type}`}>
      {/* Row header */}
      <div className="flex items-center gap-3 mb-1.5">
        <span className="text-sm font-medium text-neutral-900 min-w-[140px]">{stage.name}</span>
        <StatusBadge status={stage.status} />
        <span className="text-xs text-neutral-500 uppercase tracking-wider">{stage.type}</span>
        <span className="text-xs text-neutral-500 tabular-nums ml-auto">
          {fmtMs(stage.duration_ms)}
          {hasCost && <> · <span className="text-emerald-700">{fmtUsd(stage.cost_usd)}</span></>}
        </span>
      </div>

      {/* Bar */}
      <div className="relative h-6 bg-neutral-50 rounded overflow-hidden">
        <div className={`h-full ${style.bar} transition-all`} style={{ width: `${widthPct}%` }} />
        <div className="absolute inset-0 flex items-center justify-between px-2 text-[10px] pointer-events-none">
          <div className="flex items-center gap-2">
            {stage.provider && (
              <span className="bg-white/80 rounded px-1 text-neutral-700 flex items-center gap-1">
                <Cpu size={9} /> {stage.provider}
              </span>
            )}
            {stage.model && (
              <span className="bg-white/80 rounded px-1 text-neutral-700">
                {stage.model.replace(/^claude-/, "cl-").slice(0, 20)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {hasTokens && (
              <span className="bg-white/80 rounded px-1 text-neutral-700 tabular-nums">
                {stage.tokens_input?.toLocaleString()}↓/{stage.tokens_output?.toLocaleString()}↑
              </span>
            )}
            {hasRetries && (
              <span className="bg-amber-100 text-amber-800 rounded px-1 flex items-center gap-0.5">
                <RotateCw size={9} /> {stage.retries}
              </span>
            )}
            {hasWarnings && (
              <span className="bg-amber-100 text-amber-800 rounded px-1 flex items-center gap-0.5">
                <AlertTriangle size={9} /> {stage.warning_count}
              </span>
            )}
            {hasErrors && (
              <span className="bg-red-100 text-red-800 rounded px-1">
                ⚠ {stage.error_count}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Perf comparison + versions row */}
      <div className="flex items-center gap-3 mt-1 text-[11px] text-neutral-500 tabular-nums flex-wrap">
        {perf && perf.samples > 0 && (
          <span className="inline-flex items-center gap-2">
            <span>Current: <b>{fmtMs(perfCurrent)}</b></span>
            <span className="text-neutral-400">·</span>
            <span>Avg: {fmtMs(perf.avg_ms)}</span>
            <span className="text-neutral-400">·</span>
            <span>Best: {fmtMs(perf.best_ms)}</span>
            <span className="text-neutral-400">·</span>
            <span>Worst: {fmtMs(perf.worst_ms)}</span>
            {regressionBadge && (
              <span className={`ml-1 px-1.5 py-0.5 rounded ${regressionBadge.color}`}>
                {regressionBadge.label}
              </span>
            )}
          </span>
        )}
        {stage.prompt_version && (
          <span>prompt v<b>{stage.prompt_version}</b></span>
        )}
        {stage.template_version && (
          <span>template v<b>{stage.template_version}</b></span>
        )}
        {stage.cumulative_cost_usd != null && stage.cumulative_cost_usd > 0 && (
          <span>cum. cost <b>{fmtUsd(stage.cumulative_cost_usd)}</b></span>
        )}
      </div>

      {/* Expandable meta on hover */}
      {(Object.keys(meta).length > 0 || stage.error) && (
        <details className="mt-1.5 text-xs">
          <summary className="cursor-pointer text-neutral-500 hover:text-neutral-800 select-none">
            {stage.error ? <span className="text-red-700">Error details</span> : "Metadata"}
          </summary>
          {stage.error && (
            <pre className="mt-1 p-2 bg-red-50 text-red-800 rounded whitespace-pre-wrap font-mono text-[10px] overflow-x-auto">
              {stage.error}
            </pre>
          )}
          {Object.keys(meta).length > 0 && (
            <pre className="mt-1 p-2 bg-neutral-50 rounded whitespace-pre-wrap font-mono text-[10px] overflow-x-auto">
              {JSON.stringify(meta, null, 2)}
            </pre>
          )}
        </details>
      )}
    </div>
  );
}

// ─── Chronological Event Timeline ─────────────────────────────────────
function EventTimeline({ events }) {
  if (!events?.length) return null;
  return (
    <div className="space-y-1" data-testid="event-timeline">
      {events.map((e, i) => (
        <div key={i} className="flex items-baseline gap-3 text-xs">
          <span className="tabular-nums text-neutral-500 min-w-[80px]">
            {e.at ? new Date(e.at).toLocaleTimeString([], { hour12: false }) : ""}
          </span>
          <span className={`min-w-[60px] uppercase text-[10px] ${EVENT_LEVEL_STYLE[e.level] || "text-neutral-500"}`}>
            {e.level}
          </span>
          <span className="text-neutral-800 flex-1">{e.message}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Timeline card ───────────────────────────────────────────────
export default function RunTimeline({ runId, autoRefresh = false }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await seoApi.get(`/seo/ops/runs/${runId}/timeline`);
        if (!cancelled) { setData(r.data); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    }
    load();
    let t;
    if (autoRefresh) t = setInterval(load, 4000);
    return () => { cancelled = true; if (t) clearInterval(t); };
  }, [runId, autoRefresh]);

  const maxDurationMs = useMemo(() => {
    if (!data?.stages?.length) return 0;
    return Math.max(...data.stages.map((s) => s.duration_ms || 0));
  }, [data]);

  if (err) return (
    <Card data-testid="timeline-error">
      <CardContent className="p-6 text-sm text-red-700">Timeline error: {err}</CardContent>
    </Card>
  );
  if (!data) return (
    <Card><CardContent className="p-6 text-sm text-neutral-500">Loading timeline…</CardContent></Card>
  );

  const stages = data.stages || [];
  const events = data.events || [];
  const perf   = data.performance_by_type || {};

  return (
    <Card data-testid="run-timeline-card">
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap size={16} /> Workflow Timeline
        </CardTitle>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 text-neutral-600">
            <Clock size={12} /> {fmtMs(data.total_duration_ms)}
          </span>
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <DollarSign size={12} /> {fmtUsd(data.total_cost_usd)}
          </span>
          <span className="text-neutral-500 tabular-nums" data-testid="timeline-totals">
            {stages.length} stages
          </span>
          {(data.warnings_count > 0) && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              {data.warnings_count} warnings
            </Badge>
          )}
          {(data.errors_count > 0) && (
            <Badge variant="destructive">{data.errors_count} errors</Badge>
          )}
          {(data.retries_count > 0) && (
            <Badge variant="outline" className="text-xs">
              {data.retries_count} retries
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {stages.length === 0 ? (
          <div className="text-sm text-neutral-500 py-6 text-center">
            No stages recorded for this run yet. Timeline instrumentation started with Phase 2 —
            older runs will not show stages retroactively.
          </div>
        ) : (
          <div className="space-y-4">
            {stages.map((s, i) => (
              <StageBar key={s.id || i} stage={s} maxDurationMs={maxDurationMs}
                          performanceStats={perf} />
            ))}
          </div>
        )}

        {events.length > 0 && (
          <details className="mt-6 border-t border-neutral-200 pt-4" open data-testid="event-timeline-details">
            <summary className="cursor-pointer text-sm font-medium text-neutral-800 flex items-center gap-2">
              <Clock size={14} /> Event Timeline ({events.length})
            </summary>
            <div className="mt-3">
              <EventTimeline events={events} />
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
